import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Ambient audio (e.g. Om chanting) for the home page background.
// Browsers block autoplay-with-sound until the visitor has interacted with
// the page, so we try to play immediately and, if that's blocked, fall back
// to starting on the visitor's first click/tap/keypress anywhere on the
// page. A small floating button always lets them mute it — unsolicited
// audio must stay under the visitor's control.
export default function BackgroundAudio({ src }) {
  const { t } = useTranslation();
  const audioRef = useRef(null);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !src) return;

    audio.volume = 0.35;
    const tryPlay = () => audio.play().catch(() => {});
    tryPlay();

    // Autoplay was blocked — retry on the visitor's first interaction.
    const onFirstInteraction = () => {
      tryPlay();
      ['click', 'touchstart', 'keydown'].forEach(evt => document.removeEventListener(evt, onFirstInteraction));
    };
    ['click', 'touchstart', 'keydown'].forEach(evt => document.addEventListener(evt, onFirstInteraction, { once: true }));

    return () => {
      ['click', 'touchstart', 'keydown'].forEach(evt => document.removeEventListener(evt, onFirstInteraction));
      audio.pause();
    };
  }, [src]);

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().catch(() => {});
    audio.muted = !audio.muted;
    setMuted(audio.muted);
  };

  if (!src) return null;

  return (
    <>
      <audio ref={audioRef} src={src} loop preload="auto" />
      <button
        type="button"
        className="bg-audio-toggle"
        onClick={toggleMute}
        aria-label={muted ? t('home.unmuteAudio') : t('home.muteAudio')}
        title={muted ? t('home.unmuteAudio') : t('home.muteAudio')}
      >
        {muted ? '🔇' : '🔊'}
      </button>
    </>
  );
}
